import { z } from 'zod';
import { BauVorhabenart, Bauweise, BMAKategorie, BuildingClass, BuildingType, FederalState, FireDepartmentAccess, FireResistance, GarageTyp, HochhausTyp, Lagerart, MainUse, Priority, ProjectKind, ProjectStatus, PruefWeg, Sicherheitskategorie, SonderbauTyp, SprinklerKlasse, Verfahrensart, WaterSupplyKind, } from '../enums.js';
/**
 * Datums-Felder, die User über <input type="date"> pflegen: akzeptiert
 * 'JJJJ-MM-TT' (date-only, kommt vom Date-Input) UND volle ISO-8601-
 * Zeitstempel (kommen vom Server, z. B. '2026-06-11T00:00:00.000Z').
 * Das API konvertiert beide Formate via `new Date(value)` nach DateTime.
 */
const ISO_DATE_OR_DATETIME_RE = /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2}(\.\d+)?(Z|[+-]\d{2}:\d{2}))?$/;
export const isoDateString = z
    .string()
    .regex(ISO_DATE_OR_DATETIME_RE, 'Ungültiges Datum (erwartet JJJJ-MM-TT).')
    .refine((v) => !Number.isNaN(new Date(v).getTime()), 'Ungültiges Datum (erwartet JJJJ-MM-TT).');
export const project = z.object({
    id: z.number().int().positive(),
    customerId: z.number().int().positive(),
    createdById: z.number().int().positive().nullable().optional(),
    approvedById: z.number().int().positive().nullable().optional(),
    title: z.string(),
    description: z.string().nullable().optional(),
    address: z.string(),
    federalState: FederalState.nullable().optional(),
    status: ProjectStatus,
    priority: Priority,
    projectType: z.string().nullable().optional(),
    kind: ProjectKind.nullable().optional(),
    buildingType: BuildingType.nullable().optional(),
    buildingClass: BuildingClass.nullable().optional(),
    mainUse: MainUse.nullable().optional(),
    bauweise: Bauweise.nullable().optional(),
    bauvorhabenart: BauVorhabenart.nullable().optional(),
    isSonderbau: z.boolean().default(false),
    sonderbauTyp: SonderbauTyp.nullable().optional(),
    maxPersonenzahl: z.number().int().min(0).nullable().optional(),
    verkaufsraumFlaeche: z.number().min(0).nullable().optional(),
    hochhausTyp: HochhausTyp.nullable().optional(),
    garageTyp: GarageTyp.nullable().optional(),
    garageOffen: z.boolean().nullable().optional(),
    sicherheitskategorie: Sicherheitskategorie.nullable().optional(),
    brandlast: z.number().min(0).nullable().optional(),
    lagerart: Lagerart.nullable().optional(),
    gemarkung: z.string().nullable().optional(),
    flurNr: z.string().nullable().optional(),
    plotArea: z.number().min(0).nullable().optional(),
    buildingArea: z.number().min(0).nullable().optional(),
    // Geschosszahl: oberirdisch mind. 1, mit Unter-/Tiefgeschossen ggf. 0.
    // Negative Werte sind nie sinnvoll → Schema sperrt sie.
    floors: z.number().int().min(0).max(200).nullable().optional(),
    height: z.number().min(0).nullable().optional(),
    oberkanteAufenthaltsraum: z.number().min(0).nullable().optional(),
    abstandsflaechenKonform: z.boolean().nullable().optional(),
    brandwandZurNachbargrenze: z.boolean().nullable().optional(),
    fireResistance: FireResistance.nullable().optional(),
    escapeRoutes: z.string().nullable().optional(),
    fireSections: z.number().int().min(0).nullable().optional(),
    maxEscapeLen: z.number().min(0).nullable().optional(),
    emergencyExits: z.number().int().min(0).nullable().optional(),
    smokeDetection: z.boolean().default(false),
    sprinklerSys: z.boolean().default(false),
    sprinklerKlasse: SprinklerKlasse.nullable().optional(),
    fireAlarm: z.boolean().default(false),
    bmaKategorie: BMAKategorie.nullable().optional(),
    bmaLeitstelle: z.boolean().nullable().optional(),
    safetyLight: z.boolean().default(false),
    rwa: z.boolean().default(false),
    waterSupply: WaterSupplyKind.nullable().optional(),
    waterAmount: z.number().int().min(0).nullable().optional(),
    hydrants: z.number().int().min(0).nullable().optional(),
    fdAccess: FireDepartmentAccess.nullable().optional(),
    planner: z.string().nullable().optional(),
    verfahrensart: Verfahrensart.nullable().optional(),
    pruefWeg: PruefWeg.nullable().optional(),
    extPruefer: z.string().nullable().optional(),
    prueferId: z.number().int().positive().nullable().optional(),
    pruefDatum: isoDateString.nullable().optional(),
    freigabeDatum: isoDateString.nullable().optional(),
    startDate: isoDateString.nullable().optional(),
    deadline: isoDateString.nullable().optional(),
    completedDate: isoDateString.nullable().optional(),
    completeness: z.number().int().min(0).max(100).default(0),
    // Geo (Paket M) — async via Nominatim gefuellt.
    lat: z.number().nullable().optional(),
    lng: z.number().nullable().optional(),
    geocodedAt: z.string().datetime().nullable().optional(),
    // Gesetzt sobald ein Geocoding-Versuch lief. Ist es gesetzt aber lat/lng
    // null, liess sich die Adresse nicht aufloesen (vs. "noch nicht versucht").
    geocodeAttemptedAt: z.string().datetime().nullable().optional(),
    specialRequirements: z.string().nullable().optional(),
    notes: z.string().nullable().optional(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const createProjectRequest = project
    .omit({
    id: true,
    status: true,
    priority: true,
    completedDate: true,
    freigabeDatum: true,
    approvedById: true,
    createdById: true,
    createdAt: true,
    updatedAt: true,
    completeness: true,
    // Geo wird vom Server async befuellt — Client darf nichts mitschicken.
    lat: true,
    lng: true,
    geocodedAt: true,
    geocodeAttemptedAt: true,
})
    .extend({
    title: z.string().min(3),
    address: z.string().min(3),
    priority: Priority.default('medium'),
});
export const updateProjectRequest = createProjectRequest.partial().extend({
    /**
     * Concurrency-Token für Optimistic Locking: der `updatedAt`-Stand, den der
     * Client zuletzt geladen hat (volle ISO-8601-Zeit, wie vom Server geliefert).
     * Ist er gesetzt und stimmt nicht mehr mit der DB überein, antwortet das API
     * mit 409 `edit-conflict` statt konkurrierende Änderungen still zu
     * überschreiben (last-write-wins). Optional für Abwärtskompatibilität —
     * wird NICHT in die DB geschrieben.
     */
    expectedUpdatedAt: z.string().datetime().optional(),
});
/**
 * Zurückweisen (in_review → planning): Begründung ist Pflicht — der Ersteller
 * muss wissen, WAS nachzubessern ist. Landet als Kommentar am Projekt und im
 * Notification-Body.
 */
export const rejectProjectRequest = z.object({
    reason: z
        .string()
        .trim()
        .min(5, 'Begründung erforderlich (mind. 5 Zeichen).')
        .max(2000, 'Begründung darf max. 2000 Zeichen lang sein.'),
});
export const projectSummary = project.pick({
    id: true,
    customerId: true,
    title: true,
    address: true,
    federalState: true,
    status: true,
    priority: true,
    buildingType: true,
    buildingClass: true,
    kind: true,
    isSonderbau: true,
    sonderbauTyp: true,
    deadline: true,
    completeness: true,
    lat: true,
    lng: true,
    geocodeAttemptedAt: true,
});
